import { z } from "zod";
import { PERSON_NAME_STRIP_REGEX, PERSON_NAME_REGEX } from "../../utils/employeeSchema";
const EMPLOYMENT_APPLICATION_FLAG = ["save", "send_to_office"];
const PHONE_REGEX = /^\(\d{3}\)\d{3}-\d{4}$/;
const SSN_REGEX = /^\d{3}-\d{2}-\d{4}$/;

// Auto-formats an SSN string into xxx-xx-xxxx as the user types
export const formatSSN = (value) => {
    // Strip all non-digits
    const digits = value.replaceAll(/\D/g, "").slice(0, 9);
    if (digits.length <= 3) return digits;
    if (digits.length <= 5) return `${digits.slice(0, 3)}-${digits.slice(3)}`;
    return `${digits.slice(0, 3)}-${digits.slice(3, 5)}-${digits.slice(5)}`;
};

// Auto-formats a phone string into (XXX)XXX-XXXX as the user types
export const formatPhone = (value) => {
    if (!value) return "";
    let digits = value.replaceAll(/\D/g, "");
    if (digits.length === 11 && digits.startsWith("1")) digits = digits.slice(1);
    const raw = digits.slice(0, 10);
    if (raw.length === 0) return "";
    if (raw.length <= 3) return `(${raw}`;
    if (raw.length <= 6) return `(${raw.slice(0, 3)})${raw.slice(3)}`;
    return `(${raw.slice(0, 3)})${raw.slice(3, 6)}-${raw.slice(6)}`;
};

const phoneValidator = (msg = "Phone must be in format (XXX)XXX-XXXX") => z.string().regex(PHONE_REGEX, msg);
const ssnValidator = (msg = "SSN must be in format XXX-XX-XXXX") => z.string().regex(SSN_REGEX, msg);
const nameValidator = (
    msg = "Name may only include letters, spaces, hyphens, and ' . ,",
) => z.string().regex(PERSON_NAME_REGEX, msg);

/** Required name — trims whitespace; rejects spaces-only values. */
const requiredNameValidator = (label = "Name") =>
    z
        .string()
        .trim()
        .min(1, `${label} is required`)
        .regex(PERSON_NAME_REGEX, "Name may only include letters, spaces, hyphens, and ' . ,");

// Strips disallowed characters from name fields in real-time (aligned with PERSON_NAME_REGEX)
export const formatName = (value) => value.replaceAll(PERSON_NAME_STRIP_REGEX, "");

// Shared schemas matching backend
const employmentHistorySchema = z.object({
    employer: z.string().nullable().optional(),
    address: z.string().nullable().optional(),
    city: z.string().nullable().optional(),
    state: z.string().nullable().optional(),
    zipCode: z.string().nullable().optional(),
    telephoneNo: phoneValidator().nullable().optional().or(z.literal("")),
    immediateSupervisorTitle: z.string().nullable().optional(),
    salaryWages: z.string().nullable().optional(),
    mayContactForReference: z.union([
        z.enum(["Yes", "No", "Later"]),
        z.literal(""),
        z.null(),
        z.undefined()
    ]).optional(),
    jobTitleResponsibility: z.string().nullable().optional(),
    employmentFromDate: z.string().nullable().optional(),
    employmentToDate: z.string().nullable().optional(),
});

const educationHistorySchema = z.object({
    schoolNameAddress: z.string().min(1, "School name and address is required"),
    attendanceStartDate: z.string().min(1, "Start date is required"),
    attendanceEndDate: z.string().min(1, "End date is required"),
    majorSubject: z.string().min(1, "Major subject is required"),
    degreeCertificate: z.string().min(1, "Degree or certificate is required"),
});

const applicationReferenceSchema = z.object({
    referenceName: nameValidator().nullable().optional(),
    referenceAddress: z.string().nullable().optional(),
    referenceTitle: z.string().nullable().optional(),
    referencePhone: phoneValidator().nullable().optional().or(z.literal("")),
    yearsKnown: z.preprocess((val) => (val === "" ? undefined : val), z.coerce.number().int().positive().optional()),
});

export const applicationSchema = z.object({
    tenantId: z.coerce.number().positive().optional(),
    userId: z.coerce.number().positive().optional(), // Should be dynamic based on logged in user

    // Personal Information
    applicantName: nameValidator().min(1, "Applicant name is required"),
    ssn: ssnValidator().min(1, "SSN is required"),
    address: z.string().nullable().optional(),
    homePhone: phoneValidator().min(1, "Home phone is required"),
    cellPhone: phoneValidator().nullable().optional().or(z.literal("")),
    faxNumber: phoneValidator().nullable().optional().or(z.literal("")),
    email: z.string().email("Invalid email address").min(1, "Email is required"),
    positionAppliedFor: z.string().nullable().optional(),
    desiredPayrate: z.string().nullable().optional(),

    // License/Certificate Information (optional)
    licenseCertificateNo: z.string().nullable().optional().or(z.literal("")),
    licenseCertificateState: z.string().nullable().optional().or(z.literal("")),
    licenseCertificateExpirationDate: z.string().nullable().optional().or(z.literal("")),
    driversLicenseNo: z.string().nullable().optional().or(z.literal("")),
    driversLicenseState: z.string().nullable().optional().or(z.literal("")),
    driversLicenseExpirationDate: z.string().nullable().optional().or(z.literal("")),

    // Eligibility Questions (Frontend might use "Yes"/"No" strings, need transformation or direct boolean handling)
    // Using string for radio inputs, will transform to boolean before submission if needed, or stick to backend types if simpler
    // Backend expects booleans. we will handle conversion in the submit handler or use refine.
    // Let's us strings 'Yes'/'No' for the form state and coerce.

    usWorkEligibility: z.union([
        z.string().min(1, "Please select an answer"),
        z.null(),
        z.undefined()
    ]).refine((val) => val === "Yes" || val === "No", {
        message: "Please select an answer"
    }),
    firedOrResigned: z.union([
        z.string().min(1, "Please select an answer"),
        z.null(),
        z.undefined()
    ]).refine((val) => val === "Yes" || val === "No", {
        message: "Please select an answer"
    }),
    hasConvictionRecord: z.union([
        z.string().min(1, "Please select an answer"),
        z.null(),
        z.undefined()
    ]).refine((val) => val === "Yes" || val === "No", {
        message: "Please select an answer"
    }),

    // Emergency Contact
    emergencyContactName: nameValidator().nullable().optional(),
    emergencyContactAddress: z.string().nullable().optional(),
    emergencyContactPhone: phoneValidator().nullable().optional().or(z.literal("")),
    emergencyContactRelationship: z.string().nullable().optional(),


    // Arrays
    employmentHistories: z.array(employmentHistorySchema).optional().default([]),
    educationHistories: z
        .array(educationHistorySchema)
        .min(1, "At least one education history is required"),
    references: z.array(applicationReferenceSchema).optional().default([]),

    // Applicant Statement
    applicantSignature: z.string().min(1, "Applicant signature is required"),
    applicantSignedAt: z.string().min(1, "Applicant signed at is required"),
    reviewedBySignature: z.string().nullable().optional(),
    reviewedBySignedAt: z.string().nullable().optional(),
    reviewedBySignedBy: z.string().nullable().optional(),
    applicantStatementAccepted: z.boolean().refine(val => val === true, {
        message: "You must certify that you have read and accepted the terms."
    }),
    comments: z.string().nullable().optional(),
});

// Create Employment Application Request Schema
export const createEmploymentApplicationRequest = z.object({
    // Flag is required - determines the status
    flag: z.enum(EMPLOYMENT_APPLICATION_FLAG, {
        required_error: "Flag is required",
        invalid_type_error: `Flag must be one of: ${EMPLOYMENT_APPLICATION_FLAG.join(", ")}`,
    }),

    // Personal Information
    applicantName: nameValidator().min(1, "Applicant name is required"),
    ssn: ssnValidator().min(1, "SSN is required"),
    address: z.string().nullable().optional(),
    homePhone: phoneValidator().nullable().optional().or(z.literal("")),
    cellPhone: phoneValidator().nullable().optional().or(z.literal("")),
    faxNumber: phoneValidator().nullable().optional().or(z.literal("")),
    email: z.string().email("Invalid email address").min(1, "Email is required"),
    positionAppliedFor: z.string().nullable().optional(),
    desiredPayRate: z.string().nullable().optional(),

    // License/Certificate Information (optional)
    licenseCertificateNo: z.string().nullable().optional().or(z.literal("")),
    licenseCertificateState: z.string().nullable().optional().or(z.literal("")),
    licenseCertificateExpirationDate: z.string().nullable().optional().or(z.literal("")), // ISO date string
    driversLicenseNo: z.string().nullable().optional().or(z.literal("")),
    driversLicenseState: z.string().nullable().optional().or(z.literal("")),
    driversLicenseExpirationDate: z.string().nullable().optional().or(z.literal("")), // ISO date string

    // Eligibility Questions
    usWorkEligibility: z.boolean().refine((value) => value !== undefined, {
        message: "US Work Eligibility is required",
    }),
    firedOrResigned: z.boolean().refine((value) => value !== undefined, {
        message: "Fired or Resigned is required",
    }),
    hasConvictionRecord: z.boolean().refine((value) => value !== undefined, {
        message: "Has Conviction Record is required",
    }),

    // Emergency Contact
    emergencyContactName: nameValidator().nullable().optional(),
    emergencyContactAddress: z.string().nullable().optional(),
    emergencyContactPhone: phoneValidator().nullable().optional().or(z.literal("")),
    emergencyContactRelationship: z.string().nullable().optional(),

    // Applicant Statement
    applicantStatementAccepted: z.boolean().default(false),
    applicantStatementSignedAt: z
        .string()
        .min(1, "Applicant Statement Signed At is required"),

    // Employment History (array)
    employmentHistories: z.array(employmentHistorySchema).optional().default([]),

    // Education History (array)
    educationHistories: z.array(educationHistorySchema).optional().default([]),

    // References (array)
    references: z.array(applicationReferenceSchema).optional().default([]),
});

// Update Employment Application Request Schema
export const updateEmploymentApplicationRequest = z.object({
    applicationId: z.coerce.number().positive(),

    flag: z.enum(EMPLOYMENT_APPLICATION_FLAG, {
        required_error: "Flag is required",
        invalid_type_error: `Flag must be one of: ${EMPLOYMENT_APPLICATION_FLAG.join(", ")}`,
    }),

    // Personal Information
    applicantName: nameValidator().nullable().optional(),
    ssn: ssnValidator().nullable().optional(),
    address: z.string().nullable().optional(),
    homePhone: phoneValidator().nullable().optional().or(z.literal("")),
    cellPhone: phoneValidator().nullable().optional().or(z.literal("")),
    faxNumber: phoneValidator().nullable().optional().or(z.literal("")),
    email: z.string().email("Invalid email address").min(1, "Email is required").optional(),
    positionAppliedFor: z.string().nullable().optional(),
    desiredPayRate: z.string().nullable().optional(),

    // License/Certificate Information (optional on update)
    licenseCertificateNo: z.string().nullable().optional().or(z.literal("")),
    licenseCertificateState: z.string().nullable().optional().or(z.literal("")),
    licenseCertificateExpirationDate: z.string().nullable().optional().or(z.literal("")),
    driversLicenseNo: z.string().nullable().optional().or(z.literal("")),
    driversLicenseState: z.string().nullable().optional().or(z.literal("")),
    driversLicenseExpirationDate: z.string().nullable().optional().or(z.literal("")),

    // Eligibility Questions
    usWorkEligibility: z.boolean().optional(),
    firedOrResigned: z.boolean().optional(),
    hasConvictionRecord: z.boolean().optional(),

    // Emergency Contact
    emergencyContactName: nameValidator().nullable().optional(),
    emergencyContactAddress: z.string().nullable().optional(),
    emergencyContactPhone: phoneValidator().nullable().optional().or(z.literal("")),
    emergencyContactRelationship: z.string().nullable().optional(),

    // Applicant Statement
    applicantStatementAccepted: z.boolean().optional(),

    // Applicant signature fields
    applicantSignature: z.string().nullable().optional(),
    applicantSignedAt: z.string().nullable().optional(),


    // Employment History (array) - for updates, we'll replace all
    employmentHistories: z.array(employmentHistorySchema).optional(),

    // Education History (array) - for updates, we'll replace all
    educationHistories: z.array(educationHistorySchema).optional(),

    // References (array) - for updates, we'll replace all
    references: z.array(applicationReferenceSchema).optional(),
});

export const referenceBackgroundConsentSchema = z.object({
    tenantId: z.coerce.number().positive().optional(),
    userId: z.coerce.number().positive().optional(),
    employeeFullName: nameValidator().min(1, "Employee Full Name is required"),
    dateOfBirth: z.string().min(1, "Date of Birth is required"),
    socialSecurityNumber: ssnValidator().min(1, "Social Security Number is required"),
    currentAddress: z.string().min(1, "Current Address is required"),
    homePhone: phoneValidator().min(1, "Home Phone is required"),
    cellPhone: phoneValidator().nullable().optional().or(z.literal("")),
    applicantSignature: z.string().nullable().optional(),
    applicantSignedAt: z.string().nullable().optional(),
    reviewedBySignature: z.string().nullable().optional(),
    reviewedBySignedAt: z.string().nullable().optional(),
    reviewedBySignedBy: z.string().nullable().optional(),
    position: z.string().nullable().optional(),
    comments: z.string().nullable().optional(),
    applicantStatementAccepted: z.boolean().refine(val => val === true, {
        message: "You must certify that you have read and accepted the terms."
    }),
});

export const stateMandateComplianceSchema = z.object({
    tenantId: z.coerce.number().positive().optional(),
    userId: z.coerce.number().positive().optional(),
    clinicianName: nameValidator().min(1, "Clinician Name is required"),
    applicantSignature: z.string().nullable().optional(),
    applicantSignedAt: z.string().nullable().optional(),
    reviewedBySignature: z.string().nullable().optional(),
    reviewedBySignedAt: z.string().nullable().optional(),
    reviewedBySignedBy: z.string().nullable().optional(),
    applicantStatementAccepted: z.boolean().refine(val => val === true, {
        message: "You must acknowledge receipt and agreement"
    }),
    positionAppliedFor: z.string().nullable().optional(),
    comments: z.string().nullable().optional(),
});

export const handbookAcknowledgementSchema = z.object({
    tenantId: z.coerce.number().positive().optional(),
    userId: z.coerce.number().positive().optional(),
    employeeContractorName: nameValidator().min(1, "Employee Name is required"),
    applicantSignature: z.string().nullable().optional(),
    applicantSignedAt: z.string().nullable().optional(),
    reviewedBySignature: z.string().nullable().optional(),
    reviewedBySignedAt: z.string().nullable().optional(),
    reviewedBySignedBy: z.string().nullable().optional(),
    positionAppliedFor: z.string().nullable().optional(),
    comments: z.string().nullable().optional(),
    applicantStatementAccepted: z.boolean().refine(val => val === true, {
        message: "You must certify that you have read and understood the handbook provisions."
    }),
});

export const identificationCardSchema = z.object({
    tenantId: z.coerce.number().positive().optional(),
    userId: z.coerce.number().positive().optional(),
    employeeName: nameValidator().min(1, "Employee Name is required"),
    applicantSignature: z.string().nullable().optional(),
    applicantSignedAt: z.string().nullable().optional(),
    reviewedBySignature: z.string().nullable().optional(),
    reviewedBySignedAt: z.string().nullable().optional(),
    reviewedBySignedBy: z.string().nullable().optional(),
    formDate: z.string().nullable().optional(),
    replacementFeeAmount: z.coerce.number().default(15),
    positionAppliedFor: z.string().nullable().optional(),
    comments: z.string().nullable().optional(),
    applicantStatementAccepted: z.boolean().refine(val => val === true, {
        message: "You must certify that you have read and understood the identification card statement."
    }),
});

export const hepatitisBPolicySchema = z.object({
    tenantId: z.coerce.number().positive().optional(),
    userId: z.coerce.number().positive().optional(),
    employeeName: nameValidator().min(1, "Employee Name is required"),
    employeeTitle: z.string().nullable().optional(),
    isDecliningVaccination: z.boolean().default(false),
    hasBeenVaccinated: z.boolean().default(false),
    acknowledgementDate: z.string().min(1, "Date is required"),
    applicantSignature: z.string().nullable().optional(),
    applicantSignedAt: z.string().nullable().optional(),
    reviewedBySignature: z.string().nullable().optional(),
    reviewedBySignedAt: z.string().nullable().optional(),
    reviewedBySignedBy: z.string().nullable().optional(),
    applicantSignedByUserId: z.coerce.number().positive().optional(),
    positionAppliedFor: z.string().nullable().optional(),
    comments: z.string().nullable().optional(),
}).refine(data => data.isDecliningVaccination || data.hasBeenVaccinated, {
    message: "Please select one option.",
    path: ["isDecliningVaccination"]
});

export const confidentialityAgreementSchema = z.object({
    tenantId: z.coerce.number().positive().optional(),
    userId: z.coerce.number().positive().optional(),
    employeeName: nameValidator().min(1, "Employee Name is required"),
    agreementDate: z.string().min(1, "Date is required"),
    applicantSignature: z.string().nullable().optional(),
    applicantSignedAt: z.string().nullable().optional(),
    reviewedBySignature: z.string().nullable().optional(),
    reviewedBySignedAt: z.string().nullable().optional(),
    reviewedBySignedBy: z.string().nullable().optional(),
    applicantSignedByUserId: z.coerce.number().positive().optional(),
    positionAppliedFor: z.string().nullable().optional(),
    comments: z.string().nullable().optional(),
    applicantStatementAccepted: z.boolean().refine(val => val === true, {
        message: "You must certify that you have read and accepted the terms."
    }),
});

export const employmentAgreementSchema = z.object({
    tenantId: z.coerce.number().positive().optional(),
    userId: z.coerce.number().positive().optional(),
    employeeName: nameValidator().min(1, "Employee Name is required"),
    employeeAddress: z.string().nullable().optional(),
    employeePhone: z.string().nullable().optional(),
    position: z.string().nullable().optional(),
    effectiveDate: z.string().nullable().optional(),
    emergencyContactName: z.string().nullable().optional(),
    emergencyContactPhone: z.string().nullable().optional(),
    emergencyContactRelationship: z.string().nullable().optional(),
    hourlyRate: z.string().nullable().optional(),
    ackReadAgreement: z.boolean().optional(),
    ackUnderstandResponsibilities: z.boolean().optional(),
    ackAtWill: z.boolean().optional(),
    ackComplyPolicies: z.boolean().optional(),
    ackNoGuaranteePeriod: z.boolean().optional(),
    applicantSignature: z.string().nullable().optional(),
    applicantSignedAt: z.string().nullable().optional(),
    applicantName: z.string().nullable().optional(),
    applicantStatementAccepted: z.boolean().optional(),
    employerAuthorizedRepName: z.string().nullable().optional(),
    employerAuthorizedRepTitle: z.string().nullable().optional(),
    employerSignature: z.string().nullable().optional(),
    employerSignedAt: z.string().nullable().optional(),
    reviewedBySignature: z.string().nullable().optional(),
    reviewedBySignedAt: z.string().nullable().optional(),
    reviewedBySignedBy: z.string().nullable().optional(),
    comments: z.string().nullable().optional(),
});

const administrativeOrientationItemSchema = z.object({
    section: z.string().min(1, "Section is required"),
    itemName: z.string().min(1, "Item name is required"),
    isChecked: z.literal(true, {
        errorMap: () => ({ message: "Item must be checked" })
    }),
    employeeInitials: z.string().min(1, "Initials are required"),
    dateDiscussed: z.string().min(1, "Date is required"),
});

export const administrativeOrientationSchema = z.object({
    tenantId: z.coerce.number().positive().optional(),
    userId: z.coerce.number().positive().optional(),
    applicantSignature: z.string().min(1, "Applicant signature is required"),
    applicantSignedAt: z.string().min(1, "Applicant signed at is required"),
    reviewedBySignature: z.string().nullable().optional(),
    reviewedBySignedAt: z.string().nullable().optional(),
    reviewedBySignedBy: z.string().nullable().optional(),
    positionAppliedFor: z.string().nullable().optional(),
    items: z.array(administrativeOrientationItemSchema).optional(),
    comments: z.string().nullable().optional(),
});

const orientationChecklistItemSchema = z.object({
    itemNumber: z.coerce.number(),
    itemDescription: z.string(),
    contractorInitials: z.string().min(1, "Initials are required"),
    dateOfOrientation: z.string().min(1, "Date is required"),
});

export const orientationChecklistSchema = z.object({
    tenantId: z.coerce.number().positive().optional(),
    userId: z.coerce.number().positive().optional(),
    contractorName: nameValidator().nullable().optional(),
    date: z.string().nullable().optional(),
    applicantSignature: z.string().nullable().optional(),
    applicantSignedAt: z.string().nullable().optional(),
    reviewedBySignature: z.string().nullable().optional(),
    reviewedBySignedAt: z.string().nullable().optional(),
    reviewedBySignedBy: z.string().nullable().optional(),
    items: z.array(orientationChecklistItemSchema),
    positionAppliedFor: z.string().nullable().optional(),
    comments: z.string().nullable().optional(),
});

export const tbTestSchema = z.object({
    tenantId: z.coerce.number().positive().optional(),
    userId: z.coerce.number().positive().optional(),
    employeeName: nameValidator().min(1, "Employee Name is required"),
    socialSecurityNumber: ssnValidator().min(1, "SSN is required"),

    // Screening Questions - using union to handle null/undefined/empty strings
    hadTuberculosis: z.union([z.enum(["Yes", "No"]), z.null(), z.undefined()]).default("No"),
    hadBcgVaccine: z.union([z.enum(["Yes", "No"]), z.null(), z.undefined()]).default("No"),
    hadPositiveTbSkinTest: z.union([z.enum(["Yes", "No"]), z.null(), z.undefined()]).default("No"),

    // Symptoms
    hasProductiveCough: z.union([z.enum(["Yes", "No"]), z.null(), z.undefined()]).default("No"),
    hasNightSweats: z.union([z.enum(["Yes", "No"]), z.null(), z.undefined()]).default("No"),
    hasFever: z.union([z.enum(["Yes", "No"]), z.null(), z.undefined()]).default("No"),
    hasWeightLoss: z.union([z.enum(["Yes", "No"]), z.null(), z.undefined()]).default("No"),
    hasLossOfAppetite: z.union([z.enum(["Yes", "No"]), z.null(), z.undefined()]).default("No"),

    // Applicant Signature
    applicantSignature: z.string().nullable().optional(),
    applicantSignedAt: z.string().nullable().optional(),
    applicantSignedByUserId: z.coerce.number().positive().optional(),
    positionAppliedFor: z.string().nullable().optional(),

    // Healthcare Professional Section
    healthcareProfessionalName: z.string().nullable().optional(),
    datePlaced: z.string().nullable().optional(),
    site: z.union([z.enum(["Right", "Left"]), z.literal(""), z.null(), z.undefined()]).optional(),
    dateRead: z.string().nullable().optional(),
    indurationMm: z.coerce.number().optional().default(0),
    lotNumber: z.string().nullable().optional(),
    expirationDate: z.string().nullable().optional(),
    ppdResult: z.union([z.enum(["Positive", "Negative"]), z.literal(""), z.null(), z.undefined()]).optional(),
    administeredByName: z.string().nullable().optional(),
    administeredByTitle: z.string().nullable().optional(),
    resultsReadByName: z.string().nullable().optional(),
    resultsReadByTitle: z.string().nullable().optional(),
    reviewedBySignature: z.string().nullable().optional(),
    reviewedBySignedAt: z.string().nullable().optional(),
    reviewedBySignedBy: z.string().nullable().optional(),
    comments: z.string().nullable().optional(),
});

export const fluVaccinationSchema = z.object({
    tenantId: z.coerce.number().positive().optional(),
    userId: z.coerce.number().positive().optional(),
    employeeName: requiredNameValidator("Employee Name"),
    clinicianName: z.string().nullable().optional(),
    clinicianTitle: z.string().nullable().optional(),

    // Options (Checkbox/Radio logic might be needed on frontend, backend expects booleans)
    alreadyReceivedVaccination: z.boolean().default(false),
    requestVaccinationHomeHealth: z.boolean().default(false),
    refuseVaccination: z.boolean().default(false),

    // Refusal Section
    refusalReason: z.string().nullable().optional(),
    refusalAcknowledgmentSignature: z.string().nullable().optional(),
    refusalAcknowledgmentDate: z.string().nullable().optional(),

    // Consent Section (optional; empty string from API must not fail PERSON_NAME_REGEX)
    fluVaccinationConsentPrintName: z.preprocess(
        (val) =>
            val == null || (typeof val === "string" && val.trim() === "")
                ? undefined
                : val,
        nameValidator().nullable().optional(),
    ),
    clinicianSignature: z.string().nullable().optional(),
    locationProvidingVaccine: z.string().nullable().optional(),
    siteOfInjection: z.string().nullable().optional(),
    administeredByType: z.union([z.enum(["RN", "MD"]), z.literal("")]).nullable().optional(),
    // Empty string from API must not fail PERSON_NAME_REGEX; superRefine enforces when consent path
    administeredByName: z.preprocess(
        (val) =>
            val == null || (typeof val === "string" && val.trim() === "")
                ? undefined
                : val,
        nameValidator().nullable().optional(),
    ),
    lotNumber: z.string().nullable().optional(),
    expirationDate: z.string().nullable().optional(),

    // Applicant Signature (General)
    applicantSignature: z.string().min(1, "Applicant signature is required"),
    applicantSignedAt: z.string().min(1, "Applicant signed at is required"),
    reviewedBySignature: z.string().nullable().optional(),
    reviewedBySignedAt: z.string().nullable().optional(),
    reviewedBySignedBy: z.string().nullable().optional(),
    applicantSignedByUserId: z.coerce.number().positive().optional(),
    positionAppliedFor: z.string().nullable().optional(),
    comments: z.string().nullable().optional(),
}).superRefine((data, ctx) => {
    // Exactly one of the three vaccination options must be true
    const a = data.alreadyReceivedVaccination === true ? 1 : 0;
    const b = data.requestVaccinationHomeHealth === true ? 1 : 0;
    const c = data.refuseVaccination === true ? 1 : 0;
    if (a + b + c !== 1) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: "Exactly one option must be selected",
            path: ["alreadyReceivedVaccination"],
        });
    }

    // When alreadyReceivedVaccination or requestVaccinationHomeHealth is true, clinician fields are required
    if (data.alreadyReceivedVaccination || data.requestVaccinationHomeHealth) {
        if (!data.clinicianName?.trim()) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: "Clinician name is required",
                path: ["clinicianName"],
            });
        }
        if (!data.clinicianTitle?.trim()) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: "Clinician title is required",
                path: ["clinicianTitle"],
            });
        }
        if (!data.locationProvidingVaccine?.trim()) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: "Location providing vaccine is required",
                path: ["locationProvidingVaccine"],
            });
        }
        if (!data.siteOfInjection?.trim()) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: "Site of injection is required",
                path: ["siteOfInjection"],
            });
        }
        if (!data.administeredByType || data.administeredByType === "") {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: "Please select a clinician type (RN or MD)",
                path: ["administeredByType"],
            });
        }
        if (!data.administeredByName?.trim()) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: "Administered by name is required",
                path: ["administeredByName"],
            });
        }
        if (!data.lotNumber?.trim()) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: "Lot number is required",
                path: ["lotNumber"],
            });
        }
        if (!data.expirationDate?.trim()) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: "Expiration date is required",
                path: ["expirationDate"],
            });
        }
        if (!data.clinicianSignature?.trim()) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: "Clinician signature is required",
                path: ["clinicianSignature"],
            });
        }
    }

    // When refuseVaccination is true, refusalReason is required
    if (data.refuseVaccination) {
        if (!data.refusalReason?.trim()) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: "Refusal reason is required",
                path: ["refusalReason"],
            });
        }
    }
});

const familyHistoryItemSchema = z.object({
    familyMember: z.string().nullable().optional(),
    illness: z.string().nullable().optional(),
    relationship: z.string().nullable().optional(),
});

export const employeeHealthExaminationSchema = z.object({
    tenantId: z.coerce.number().positive().optional(),
    userId: z.coerce.number().positive().optional(),
    employeeName: nameValidator().min(1, "Employee Name is required"),
    position: z.string().nullable().optional(),
    patientTitle: z.string().nullable().optional(),
    patientName: nameValidator().nullable().optional(),

    conditionException: z.string().nullable().optional(),
    noHazardStatement: z.string().nullable().optional(),

    // Vitals / Physical
    height: z.string().nullable().optional(),
    weight: z.string().nullable().optional(),
    bloodPressure: z.string().nullable().optional(),
    pulse: z.string().nullable().optional(),

    // TB / PPD
    ppdTestResult: z.union([
        z.string().min(1, "Please select a result"),
        z.null(),
        z.undefined()
    ]).refine((val) => val === "Positive" || val === "Negative", {
        message: "Please select a result"
    }),
    mantoux: z.string().nullable().optional(),
    ppdDateAdministered: z.string().nullable().optional(),
    chestXrayResult: z.union([
        z.string().min(1, "Please select a result"),
        z.null(),
        z.undefined()
    ]).refine((val) => val === "Positive" || val === "Negative", {
        message: "Please select a result"
    }),
    chestXrayDateAdministered: z.string().nullable().optional(),

    // Examination Fields
    adenopathy: z.string().nullable().optional(),
    reflexes: z.string().nullable().optional(),
    eyes: z.string().nullable().optional(),
    hearing: z.string().nullable().optional(),
    nose: z.string().nullable().optional(),
    throat: z.string().nullable().optional(),
    tongue: z.string().nullable().optional(),
    teeth: z.string().nullable().optional(),
    abdomen: z.string().nullable().optional(),
    rectal: z.string().nullable().optional(),
    heartSize: z.string().nullable().optional(),
    murmur: z.string().nullable().optional(),
    rhythm: z.string().nullable().optional(),
    arteries: z.string().nullable().optional(),
    chestBreathSounds: z.string().nullable().optional(),
    resonance: z.string().nullable().optional(),

    // Physician Info
    physicianName: nameValidator().nullable().optional(),
    physicianPhone: phoneValidator().nullable().optional().or(z.literal("")),
    physicianAddress: z.string().nullable().optional(),
    physicianSignature: z.string().nullable().optional(),
    physicianDate: z.string().nullable().optional(),

    // Signatures
    applicantSignature: z.string().nullable().optional(),
    applicantSignedAt: z.string().nullable().optional(),
    reviewedBySignature: z.string().nullable().optional(),
    reviewedBySignedAt: z.string().nullable().optional(),
    reviewedBySignedBy: z.string().nullable().optional(),

    // Arrays
    familyHistories: z.array(familyHistoryItemSchema).optional().default([]),
    positionAppliedFor: z.string().nullable().optional(),
    comments: z.string().nullable().optional(),
});

export const governmentFormSchema = z.object({
    tenantId: z.coerce.number().positive().optional(),
    userId: z.coerce.number().positive().optional(),
    applicantSignature: z.string().nullable().optional(),
    applicantSignedAt: z.string().nullable().optional(),
    reviewedBySignature: z.string().nullable().optional(),
    reviewedBySignedAt: z.string().nullable().optional(),
    reviewedBySignedBy: z.string().nullable().optional(),
    positionAppliedFor: z.string().nullable().optional(),
    comments: z.string().nullable().optional(),
});

export const contractorServiceAgreementSchema = z.object({
    tenantId: z.coerce.number().positive().optional(),
    userId: z.coerce.number().positive().optional(),
    serviceAgreementDate: z.string().min(1, "Agreement Date is required"),
    agencyName: z.string().nullable().optional(),
    contractorName: nameValidator().min(1, "Contractor Name is required"),
    agencyPrincipalAddress: z.string().nullable().optional(),
    agencyEmail: z.string().nullable().optional(),
    agreementSubject: z.string().nullable().optional(),
    serviceCounties: z.string().nullable().optional(),
    effectiveDate: z.string().nullable().optional(),
    applicantSignature: z.string().nullable().optional(),
    applicantSignedAt: z.string().nullable().optional(),
    reviewedBySignature: z.string().nullable().optional(),
    reviewedBySignedAt: z.string().nullable().optional(),
    reviewedBySignedBy: z.string().nullable().optional(),
    positionAppliedFor: z.string().nullable().optional(),
    comments: z.string().nullable().optional(),
}).passthrough();